import { useRef, type KeyboardEvent } from 'react';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { differenceInCalendarDays, formatISO, parseISO } from 'date-fns';
import { ChevronDown } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';
import { mapPersonasToOptions, personasQuery, type PersonaOption } from '@/entities/persona';
import { useWorkspaceTagSuggestions } from '@/entities/project-tag';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import {
  DESCRIPTION_MAX_LENGTH,
  getMarketLabel,
  isFieldRequired,
  MARKET_OPTIONS,
  PROJECT_TYPE_OPTIONS,
  STATUS_OPTIONS,
  TITLE_MAX_LENGTH,
  type CreateProjectFormData,
} from '@/features/edit-project/model/schema';
import { PersonaAvatar } from '@/shared/ui/PersonaAvatar';
import { CharCounter, DatePicker, TagInput } from '@/shared/ui';
import { useTranslation } from 'react-i18next';

interface EditProjectFormFieldsProps {
  disabled: boolean;
}

const DATE_PICKER_WINDOW_YEARS = 5;
const today = new Date();
const dateRangeStart = new Date(today.getFullYear() - DATE_PICKER_WINDOW_YEARS, 0, 1);
const dateRangeEnd = new Date(today.getFullYear() + DATE_PICKER_WINDOW_YEARS, 11, 31);

/**
 * Phase 6 — full v4 Form composition.
 *
 * Mirrors ReviewDetailsStep: replaces every Controller +
 * SelectTextField/TagInputField/DateTextField pair with v4 <FormField>
 * + inline Shadcn primitives. Zero MUI imports — direct OR transitive.
 *
 * Reads `control` from `useFormContext()` per the v4 Form pattern (AD-10),
 * so the caller MUST wrap this component in `<Form {...form}>` (or RHF
 * `<FormProvider {...form}>`).
 */
export function EditProjectFormFields(props: EditProjectFormFieldsProps) {
  const { t } = useTranslation('projects');

  const { control } = useFormContext<CreateProjectFormData>();
  const tagsAnchorRef = useRef<HTMLDivElement | null>(null);
  const marketsAnchorRef = useRef<HTMLDivElement | null>(null);
  const [startDate, deadline] = useWatch({ control, name: ['startDate', 'deadline'] });
  const startDateObj = startDate ? parseISO(startDate) : null;
  const deadlineObj = deadline ? parseISO(deadline) : null;
  const durationDays =
    startDateObj && deadlineObj ? differenceInCalendarDays(deadlineObj, startDateObj) : null;
  const workspaceId = useCurrentWorkspaceId();
  const { data: personas = [] } = useQuery(personasQuery(workspaceId ?? undefined));
  const { suggestions: tagOptions } = useWorkspaceTagSuggestions(workspaceId);
  const personaOptions = mapPersonasToOptions(personas);

  const getPersonaById = (id: string): PersonaOption | undefined =>
    personaOptions.find((p) => p.id === id);

  return (
    <div className="flex flex-col gap-4">
      <FormField
        control={control}
        name="type"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t('editProject.editProjectFormFields.projectType')}
              {isFieldRequired('type') && (
                <span aria-hidden="true" className="text-destructive">
                  *
                </span>
              )}
            </FormLabel>
            <Select value={field.value} onValueChange={field.onChange} disabled={props.disabled}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue
                    placeholder={t('editProject.editProjectFormFields.selectAProjectType')}
                  />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="pointer-events-auto">
                {PROJECT_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="title"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t('editProject.editProjectFormFields.projectTitle')}
              {isFieldRequired('title') && (
                <span aria-hidden="true" className="text-destructive">
                  *
                </span>
              )}
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder={t('editProject.editProjectFormFields.addProjectTitle')}
                disabled={props.disabled}
                // Workaround for v4 DS token inconsistency: <Input> defaults
                // to bg-input (gray) while sibling <Textarea> uses
                // bg-background (white). Tailwind merge replaces the base
                // bg-input cleanly so Title visually matches Description.
                // Apply the same override to every project-form Input
                // until v4 normalizes the token.
                className="bg-background"
              />
            </FormControl>
            <div className="flex items-center justify-between gap-2">
              <FormMessage />
              <CharCounter current={field.value.length} max={TITLE_MAX_LENGTH} />
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t('editProject.editProjectFormFields.projectDescription')}
              {isFieldRequired('description') && (
                <span aria-hidden="true" className="text-destructive">
                  *
                </span>
              )}
            </FormLabel>
            <FormControl>
              <Textarea
                {...field}
                placeholder={t('editProject.editProjectFormFields.addProjectDescription')}
                rows={3}
                disabled={props.disabled}
              />
            </FormControl>
            <div className="flex items-center justify-between gap-2">
              <FormMessage />
              <CharCounter current={field.value.length} max={DESCRIPTION_MAX_LENGTH} />
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="tags"
        render={({ field }) => {
          const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key !== 'Enter') return;
            const target = event.currentTarget;
            const text = target.value.trim();
            if (text === '') return;
            if ((field.value ?? []).includes(text)) {
              target.value = '';
              return;
            }
            event.preventDefault();
            field.onChange([...(field.value ?? []), text]);
            target.value = '';
          };

          return (
            <FormItem>
              <FormLabel>{t('editProject.editProjectFormFields.tags')}</FormLabel>
              <FormControl>
                <Combobox<string, true>
                  multiple
                  value={field.value ?? []}
                  onValueChange={field.onChange}
                  items={tagOptions}
                  disabled={props.disabled}
                  itemToStringLabel={(item) => item}
                  itemToStringValue={(item) => item}
                >
                  <ComboboxChips ref={tagsAnchorRef}>
                    {(field.value ?? []).map((tag, index) => (
                      <ComboboxChip
                        key={`${tag}:${index}`}
                        aria-label={t('editProject.editProjectFormFields.removeTag', { tag })}
                        className="rounded-full bg-accent px-2.5 text-accent-foreground"
                      >
                        {tag}
                      </ComboboxChip>
                    ))}
                    <ComboboxChipsInput
                      placeholder={t('editProject.editProjectFormFields.typeAndHitEnterTo')}
                      disabled={props.disabled}
                      onKeyDown={handleKeyDown}
                    />
                    <ChevronDown
                      aria-hidden="true"
                      className="ml-auto size-4 shrink-0 text-foreground-subtle pointer-events-none"
                    />
                  </ComboboxChips>
                  <ComboboxContent anchor={tagsAnchorRef} className="pointer-events-auto">
                    <ComboboxList>
                      <ComboboxCollection>
                        {(item: string) => (
                          <ComboboxItem key={item} value={item}>
                            {item}
                          </ComboboxItem>
                        )}
                      </ComboboxCollection>
                      <ComboboxEmpty>
                        {t('editProject.editProjectFormFields.noResults')}
                      </ComboboxEmpty>
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />

      <FormField
        control={control}
        name="markets"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t('editProject.editProjectFormFields.market')}
              {isFieldRequired('markets') && (
                <span aria-hidden="true" className="text-destructive">
                  *
                </span>
              )}
            </FormLabel>
            <FormControl>
              <Combobox<string, true>
                multiple
                value={field.value}
                onValueChange={field.onChange}
                items={[...MARKET_OPTIONS]}
                disabled={props.disabled}
                itemToStringLabel={getMarketLabel}
                itemToStringValue={(item) => item}
              >
                <ComboboxChips ref={marketsAnchorRef}>
                  {field.value.map((market, index) => (
                    <ComboboxChip
                      key={`${market}:${index}`}
                      aria-label={t('editProject.editProjectFormFields.removeMarket', {
                        market: getMarketLabel(market),
                      })}
                      className="rounded-full bg-accent px-2.5 text-accent-foreground"
                    >
                      {getMarketLabel(market)}
                    </ComboboxChip>
                  ))}
                  <ComboboxChipsInput
                    placeholder={t('editProject.editProjectFormFields.selectMarket')}
                    disabled={props.disabled}
                  />
                  <ChevronDown
                    aria-hidden="true"
                    className="ml-auto size-4 shrink-0 text-foreground-subtle pointer-events-none"
                  />
                </ComboboxChips>
                <ComboboxContent anchor={marketsAnchorRef} className="pointer-events-auto">
                  <ComboboxList>
                    <ComboboxCollection>
                      {(item: string) => (
                        <ComboboxItem key={item} value={item}>
                          {getMarketLabel(item)}
                        </ComboboxItem>
                      )}
                    </ComboboxCollection>
                    <ComboboxEmpty>
                      {t('editProject.editProjectFormFields.noResults')}
                    </ComboboxEmpty>
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="status"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('editProject.editProjectFormFields.projectStatus')}</FormLabel>
            <Select
              value={field.value ?? ''}
              onValueChange={field.onChange}
              disabled={props.disabled}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={t('editProject.editProjectFormFields.selectStatus')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="pointer-events-auto">
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="flex flex-col gap-1">
        <div className="grid grid-cols-2 gap-2">
          <FormField
            control={control}
            name="startDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('editProject.editProjectFormFields.startDate')}</FormLabel>
                <FormControl>
                  <DatePicker
                    value={field.value ? parseISO(field.value) : null}
                    onChange={(date) => {
                      field.onChange(date ? formatISO(date, { representation: 'date' }) : '');
                    }}
                    disabled={props.disabled}
                  >
                    <DatePicker.Trigger
                      placeholder={t('editProject.editProjectFormFields.addDates')}
                    />
                    <DatePicker.Content
                      startMonth={dateRangeStart}
                      endMonth={dateRangeEnd}
                      disabledDates={deadlineObj ? { after: deadlineObj } : undefined}
                    />
                  </DatePicker>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="deadline"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('editProject.editProjectFormFields.deadline')}</FormLabel>
                <FormControl>
                  <DatePicker
                    value={field.value ? parseISO(field.value) : null}
                    onChange={(date) => {
                      field.onChange(date ? formatISO(date, { representation: 'date' }) : '');
                    }}
                    disabled={props.disabled}
                  >
                    <DatePicker.Trigger
                      placeholder={t('editProject.editProjectFormFields.addDates')}
                    />
                    <DatePicker.Content
                      startMonth={dateRangeStart}
                      endMonth={dateRangeEnd}
                      disabledDates={startDateObj ? { before: startDateObj } : undefined}
                    />
                  </DatePicker>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {durationDays !== null && durationDays >= 0 && (
          <p className="text-xs text-muted-foreground">
            {t('editProject.editProjectFormFields.duration', { count: durationDays })}
          </p>
        )}
      </div>

      <FormField
        control={control}
        name="audienceIds"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('editProject.editProjectFormFields.audiences')}</FormLabel>
            <FormControl>
              <TagInput<PersonaOption>
                value={
                  (field.value ?? [])
                    .map((id) => getPersonaById(id))
                    .filter(Boolean) as PersonaOption[]
                }
                onChange={(newValue) => field.onChange(newValue.map((v) => v.id))}
                options={personaOptions}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.id}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                disabled={props.disabled}
              >
                <TagInput.Chips<PersonaOption>
                  placeholder={t('editProject.editProjectFormFields.searchForAudiences')}
                  renderChip={(persona, onRemove) => (
                    <TagInput.Chip
                      key={persona.id}
                      variant="default"
                      avatar={
                        <PersonaAvatar
                          name={persona.label}
                          photoAssetId={persona.photoAssetId}
                          size={24}
                          circular
                        />
                      }
                      label={persona.label}
                      removeLabel={t('editProject.editProjectFormFields.removePersona', {
                        label: persona.label,
                      })}
                      onRemove={onRemove}
                    />
                  )}
                />
                <TagInput.Content<PersonaOption>
                  renderOption={(option) => (
                    <div className="flex items-center gap-3">
                      <PersonaAvatar
                        name={option.label}
                        photoAssetId={option.photoAssetId}
                        size={32}
                        circular
                      />
                      <span className="text-sm font-normal text-foreground">{option.label}</span>
                    </div>
                  )}
                />
              </TagInput>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
